import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Cloud as CloudIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Code as CodeIcon,
  Storage as StorageIcon,
  Build as BuildIcon,
  Timeline as TimelineIcon,
  Autorenew as AutorenewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ModernizationDevSecOpsCapability: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/dev-technology-contracts');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to Dev Technology Contracts"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Modernization & DevSecOps Capability
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Legacy Modernization, Cloud Migration, and Secure Development Operations
          </Typography>
        </Box>
      </Box>

      {/* Capability Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Capability Overview
          </Typography>
          <Typography variant="body1" paragraph>
            Dev Technology Group delivers comprehensive modernization and DevSecOps services to transform legacy government 
            systems into modern, cloud-native applications with integrated security and automated operations. Our expertise 
            spans mainframe-to-cloud migrations, containerization, CI/CD pipeline implementation, and infrastructure as code, 
            enabling agencies to achieve greater agility, scalability, and security while reducing operational costs and 
            technical debt.
          </Typography>
        </CardContent>
      </Card>

      {/* Legacy Modernization */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AutorenewIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Legacy Modernization Services
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StorageIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Mainframe to Cloud Migration
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Transforming legacy mainframe systems to modern cloud platforms.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Assessment:</strong> Legacy system analysis and migration planning</Typography></li>
                <li><Typography variant="body2"><strong>Rehosting:</strong> Lift-and-shift to cloud infrastructure</Typography></li>
                <li><Typography variant="body2"><strong>Replatforming:</strong> Minimal changes for cloud optimization</Typography></li>
                <li><Typography variant="body2"><strong>Refactoring:</strong> Re-architecting for cloud-native</Typography></li>
                <li><Typography variant="body2"><strong>Data Migration:</strong> Database and data warehouse migration</Typography></li>
                <li><Typography variant="body2"><strong>Testing:</strong> Comprehensive validation and performance testing</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CodeIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Application Modernization
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Updating legacy applications with modern technologies.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Code Refactoring:</strong> Improving legacy codebase quality</Typography></li>
                <li><Typography variant="body2"><strong>Framework Upgrades:</strong> Migrating to modern frameworks</Typography></li>
                <li><Typography variant="body2"><strong>API Modernization:</strong> RESTful and microservices architecture</Typography></li>
                <li><Typography variant="body2"><strong>UI/UX Refresh:</strong> Modern, responsive interfaces</Typography></li>
                <li><Typography variant="body2"><strong>Performance Optimization:</strong> Speed and scalability improvements</Typography></li>
                <li><Typography variant="body2"><strong>Technical Debt Reduction:</strong> Addressing legacy issues</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CloudIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Cloud Migration Strategies
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Strategic approaches to cloud adoption and migration.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>6 R's Strategy:</strong> Rehost, Replatform, Refactor, Repurchase, Retire, Retain</Typography></li>
                <li><Typography variant="body2"><strong>Hybrid Cloud:</strong> On-premises and cloud integration</Typography></li>
                <li><Typography variant="body2"><strong>Multi-Cloud:</strong> AWS, Azure, Google Cloud strategies</Typography></li>
                <li><Typography variant="body2"><strong>Cloud-Native:</strong> Microservices, containers, serverless</Typography></li>
                <li><Typography variant="body2"><strong>Cost Optimization:</strong> Right-sizing and resource management</Typography></li>
                <li><Typography variant="body2"><strong>Governance:</strong> Cloud policies and compliance</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimelineIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  Modernization Roadmap
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Phased approach to system modernization.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Phase 1:</strong> Assessment and planning</Typography></li>
                <li><Typography variant="body2"><strong>Phase 2:</strong> Proof of concept and pilot</Typography></li>
                <li><Typography variant="body2"><strong>Phase 3:</strong> Incremental migration</Typography></li>
                <li><Typography variant="body2"><strong>Phase 4:</strong> Optimization and scaling</Typography></li>
                <li><Typography variant="body2"><strong>Phase 5:</strong> Decommissioning legacy systems</Typography></li>
                <li><Typography variant="body2"><strong>Continuous:</strong> Monitoring and improvement</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* DevSecOps */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        DevSecOps Practices & Tools
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BuildIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                  CI/CD Pipelines
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Automated build, test, and deployment workflows.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Jenkins:</strong> Open-source automation server</Typography></li>
                <li><Typography variant="body2"><strong>GitLab CI/CD:</strong> Integrated DevOps platform</Typography></li>
                <li><Typography variant="body2"><strong>GitHub Actions:</strong> Workflow automation</Typography></li>
                <li><Typography variant="body2"><strong>Azure DevOps:</strong> Microsoft DevOps services</Typography></li>
                <li><Typography variant="body2"><strong>AWS CodePipeline:</strong> Continuous delivery service</Typography></li>
                <li><Typography variant="body2"><strong>Automated Testing:</strong> Unit, integration, E2E tests</Typography></li>
                <li><Typography variant="body2"><strong>Deployment Automation:</strong> Blue-green, canary deployments</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Security Integration
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Security built into every stage of development.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>SAST:</strong> Static application security testing (SonarQube, Checkmarx)</Typography></li>
                <li><Typography variant="body2"><strong>DAST:</strong> Dynamic application security testing (OWASP ZAP)</Typography></li>
                <li><Typography variant="body2"><strong>SCA:</strong> Software composition analysis (Snyk, Black Duck)</Typography></li>
                <li><Typography variant="body2"><strong>Container Scanning:</strong> Image vulnerability scanning (Clair, Trivy)</Typography></li>
                <li><Typography variant="body2"><strong>Secrets Management:</strong> HashiCorp Vault, AWS Secrets Manager</Typography></li>
                <li><Typography variant="body2"><strong>Compliance Scanning:</strong> Policy enforcement and validation</Typography></li>
                <li><Typography variant="body2"><strong>Security Gates:</strong> Automated security approvals</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SpeedIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Automated Testing
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Comprehensive test automation across all levels.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Unit Testing:</strong> JUnit, Jest, pytest, NUnit</Typography></li>
                <li><Typography variant="body2"><strong>Integration Testing:</strong> API and service integration tests</Typography></li>
                <li><Typography variant="body2"><strong>E2E Testing:</strong> Selenium, Cypress, Playwright</Typography></li>
                <li><Typography variant="body2"><strong>Performance Testing:</strong> JMeter, Gatling, k6</Typography></li>
                <li><Typography variant="body2"><strong>Load Testing:</strong> Stress and scalability testing</Typography></li>
                <li><Typography variant="body2"><strong>Test Coverage:</strong> 80%+ code coverage targets</Typography></li>
                <li><Typography variant="body2"><strong>Test Reporting:</strong> Automated test result dashboards</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimelineIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Monitoring & Observability
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Real-time monitoring and performance insights.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>APM:</strong> Application performance monitoring (New Relic, Dynatrace)</Typography></li>
                <li><Typography variant="body2"><strong>Log Management:</strong> Splunk, ELK Stack, CloudWatch</Typography></li>
                <li><Typography variant="body2"><strong>Metrics:</strong> Prometheus, Grafana dashboards</Typography></li>
                <li><Typography variant="body2"><strong>Distributed Tracing:</strong> Jaeger, Zipkin</Typography></li>
                <li><Typography variant="body2"><strong>Alerting:</strong> PagerDuty, Opsgenie integration</Typography></li>
                <li><Typography variant="body2"><strong>Incident Management:</strong> ServiceNow, Jira Service Management</Typography></li>
                <li><Typography variant="body2"><strong>SLA Monitoring:</strong> Uptime and performance tracking</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cloud Services */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <CloudIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Cloud Services & Platforms
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cloud Platform</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Core Services</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Use Cases</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Amazon Web Services (AWS)</strong></TableCell>
              <TableCell>EC2, S3, RDS, Lambda, EKS, CloudFormation</TableCell>
              <TableCell>Compute, storage, databases, serverless, containers</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Microsoft Azure</strong></TableCell>
              <TableCell>VMs, Blob Storage, SQL Database, Functions, AKS</TableCell>
              <TableCell>Enterprise applications, hybrid cloud, .NET workloads</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Google Cloud Platform (GCP)</strong></TableCell>
              <TableCell>Compute Engine, Cloud Storage, BigQuery, GKE</TableCell>
              <TableCell>Data analytics, machine learning, Kubernetes</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Hybrid Cloud</strong></TableCell>
              <TableCell>VMware, OpenStack, Azure Arc, AWS Outposts</TableCell>
              <TableCell>On-premises integration, data sovereignty</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Containerization & Orchestration */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Containerization & Orchestration
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
                Docker Containerization
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Container image creation and optimization</Typography></li>
                <li><Typography variant="body2">Multi-stage builds for smaller images</Typography></li>
                <li><Typography variant="body2">Docker Compose for local development</Typography></li>
                <li><Typography variant="body2">Container registry management (ECR, ACR, Docker Hub)</Typography></li>
                <li><Typography variant="body2">Image scanning and vulnerability management</Typography></li>
                <li><Typography variant="body2">Best practices for security and performance</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                Kubernetes Orchestration
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">EKS, AKS, GKE managed Kubernetes services</Typography></li>
                <li><Typography variant="body2">Deployment strategies (rolling, blue-green, canary)</Typography></li>
                <li><Typography variant="body2">Service mesh (Istio, Linkerd) for microservices</Typography></li>
                <li><Typography variant="body2">Auto-scaling (HPA, VPA, cluster autoscaler)</Typography></li>
                <li><Typography variant="body2">Ingress controllers and load balancing</Typography></li>
                <li><Typography variant="body2">Helm charts for package management</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Infrastructure as Code */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Infrastructure as Code (IaC)
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#7B1FA2' }}>
                Terraform
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Multi-cloud infrastructure provisioning</Typography></li>
                <li><Typography variant="body2">State management and remote backends</Typography></li>
                <li><Typography variant="body2">Modules for reusable infrastructure</Typography></li>
                <li><Typography variant="body2">Terraform Cloud/Enterprise</Typography></li>
                <li><Typography variant="body2">Policy as code (Sentinel, OPA)</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#F57C00' }}>
                AWS CloudFormation
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">AWS-native infrastructure templates</Typography></li>
                <li><Typography variant="body2">Stack management and updates</Typography></li>
                <li><Typography variant="body2">StackSets for multi-account deployment</Typography></li>
                <li><Typography variant="body2">Change sets for preview</Typography></li>
                <li><Typography variant="body2">Drift detection and remediation</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
                Azure ARM Templates
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Azure Resource Manager templates</Typography></li>
                <li><Typography variant="body2">Bicep for simplified syntax</Typography></li>
                <li><Typography variant="body2">Template specs and deployment stacks</Typography></li>
                <li><Typography variant="body2">Azure Policy integration</Typography></li>
                <li><Typography variant="body2">Blueprint for governance</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Benefits & Outcomes */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Modernization & DevSecOps Benefits
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Business Benefits:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Faster time to market (50-70% reduction)</Typography></li>
                <li><Typography variant="body2">Reduced operational costs (30-50% savings)</Typography></li>
                <li><Typography variant="body2">Improved system reliability (99.9%+ uptime)</Typography></li>
                <li><Typography variant="body2">Enhanced scalability and performance</Typography></li>
                <li><Typography variant="body2">Better resource utilization and efficiency</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Technical Benefits:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Automated deployments (multiple per day)</Typography></li>
                <li><Typography variant="body2">Integrated security throughout SDLC</Typography></li>
                <li><Typography variant="body2">Reduced technical debt and maintenance</Typography></li>
                <li><Typography variant="body2">Improved code quality and test coverage</Typography></li>
                <li><Typography variant="body2">Cloud-native architecture and portability</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Metrics */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SpeedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        DevSecOps Metrics & Performance
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                50-70%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Faster Delivery
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Time to market reduction
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                99.9%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Uptime SLA
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                System availability
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                30-50%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cost Savings
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Operational cost reduction
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                Multiple
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Daily Deployments
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Automated releases
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ModernizationDevSecOpsCapability;
